import { useCallback, useEffect, useState } from "react";
import { ExemptedStudent, ThemeWrapper, VerityInstituteSettings, ExemptionSection } from "@verity/components";
import { CustomSettingData } from "../../settings-page";
import { OptionRelatedInstituteSettings, ProctoringOptionProfileV2 } from "@verity/proctoring-option/types";
import apiClient from "../../../../helpers/api-client";
import { Box } from "@mui/material";
import { UseFetchProctoringDataReturn } from "../useFetchProctoringData";
import { coreToast } from "@verity/components/core";

export type InstitutionAccommodationProps = {
	profilesMap: Map<number, ProctoringOptionProfileV2>;
	optionRelatedInstituteSettings: OptionRelatedInstituteSettings | undefined;
	verityInstituteSettings: VerityInstituteSettings;
	exemptionInfo: {
		nonExemptRoster: Map<number, ExemptedStudent>;
		exemptRoster: Map<number, ExemptedStudent>;
	};
} & UseFetchProctoringDataReturn;

export const InstitutionAccommodationInput = (props: {
	loadData: InstitutionAccommodationProps;
	setting: CustomSettingData<Map<number, ExemptedStudent>>;
	onSettingChange: (value: Map<number, ExemptedStudent>) => void;
}) => {
	const [uploadedExemptedStudentsMap, setUploadedExemptedStudentsMap] = useState<Map<number, ExemptedStudent>>(
		new Map<number, ExemptedStudent>(props.loadData.exemptionInfo.exemptRoster),
	);

	const [profilesMap, setProfilesMap] = useState<Map<number, ProctoringOptionProfileV2>>(
		new Map<number, ProctoringOptionProfileV2>(props.loadData.profilesMap),
	);

	useEffect(() => {
		props.onSettingChange(uploadedExemptedStudentsMap);
	}, [uploadedExemptedStudentsMap, props]);

	const [exemptionInfo, setExemptionInfo] = useState<{
		nonExemptRoster: Map<number, ExemptedStudent>;
		exemptRoster: Map<number, ExemptedStudent>;
	}>(props.loadData.exemptionInfo);

	const handleDeleteProfile = useCallback(
		async (profileId: number) => {
			try {
				await apiClient.proctoringProfile.delete(profileId);
				const profiles = await apiClient.proctoringProfile.getAll({});
				const refreshProfileMap = new Map(
					profiles
						.filter(
							(profile) =>
								(profile.proctoringOption.isExtension &&
									props.loadData.verityInstituteSettings.allowExtensionPlatform) ||
								(profile.proctoringOption.isLockdown && props.loadData.verityInstituteSettings.allowLockdownPlatform) ||
								(profile.proctoringOption.isMobile && props.loadData.verityInstituteSettings.allowMobilePlatform),
						)
						.map((x) => [x.id, x]),
				);
				setProfilesMap(refreshProfileMap);
				coreToast.success("The proctoring profile has been successfully deleted.");
			} catch (error) {
				console.error(error);
				// TODO TRANSLATION
				coreToast.error("The proctoring profile could not be deleted.");
			}
		},
		[
			props.loadData.verityInstituteSettings.allowExtensionPlatform,
			props.loadData.verityInstituteSettings.allowLockdownPlatform,
			props.loadData.verityInstituteSettings.allowMobilePlatform,
		],
	);

	return (
		<ThemeWrapper>
			<Box overflow={"auto"}>
				<ExemptionSection
					loading={false}
					canDeleteProfile={() => true}
					profilesMap={profilesMap}
					exemptionInfo={exemptionInfo}
					onRosterChange={(newRoster) => {
						setExemptionInfo(newRoster);
						if (newRoster.exemptRoster.size >= 0 && newRoster.nonExemptRoster.size >= 0) {
							setUploadedExemptedStudentsMap(newRoster.exemptRoster);
						}
					}}
					onDelete={(profile) => handleDeleteProfile(profile.id)}
					editInstitutionExemptions={true}
					enforcedProfilesOnly={false}
					optionRelatedInstituteSettings={props.loadData.optionRelatedInstituteSettings}
					verityInstituteSettings={props.loadData.verityInstituteSettings}
					proctoringData={{ applications: props.loadData.applications, extensions: props.loadData.extensions }}
				/>
			</Box>
		</ThemeWrapper>
	);
};
